<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { dayjsKey } from '@/keys';
import { useBookingViewStore } from '@/stores/booking-view-store';
import { useUserStore } from '@/stores/user-store';
import EventPopup from '@/elements/EventPopup.vue';
import { initialEventPopupData, showEventPopup, timeFormat } from '@/utils';
import { Appointment, EventPopup as EventPopupType, GridElement, GridTimeSlot, RemoteEvent, Slot } from '@/models';
import { BookingStatus, DateFormatStrings } from '@/definitions';
import { Dayjs } from 'dayjs';
import LoadingSpinner from '@/elements/LoadingSpinner.vue';
import { isDark as isDarkMode } from '@/composables/useColourScheme';

// Constants for grid calculations
const ROW_HEIGHT_PX = 60; // Matches minmax(60px, min-content) in grid-template-rows

interface Props {
  activeDateRange: {
    start: string;
    end: string;
  };
  events?: RemoteEvent[];
  pendingAppointments?: Appointment[];
  selectableSlots?: Slot[];
  isLoading?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  events: () => [] as RemoteEvent[],
  pendingAppointments: () => [] as Appointment[],
  selectableSlots: () => [] as Slot[],
  isLoading: false,
  disabled: false,
});

const emit = defineEmits(['event-selected']);

const dj = inject(dayjsKey);

const userStore = useUserStore();

const { selectedEvent } = storeToRefs(useBookingViewStore());

const calendarContainerRef = ref<HTMLElement | null>(null);
const calendarHeaderRef = ref<HTMLElement | null>(null);
const popup = ref<EventPopupType>({ ...initialEventPopupData });

const hourFormat = computed(() => timeFormat());

// Scroll sync state
let pendingScrollSync: number | null = null;
let lastScrollSource: 'header' | 'body' | null = null;
let isProgrammaticScroll = false;

/**
 * Syncs horizontal scroll between header and body for mobile
 * Uses requestAnimationFrame to batch updates and prevent layout thrashing
 */
function syncScroll(source: 'header' | 'body') {
  // Ignore scroll events triggered by our own programmatic updates
  if (isProgrammaticScroll) return;

  lastScrollSource = source;

  // If we already have a pending frame, don't schedule another
  if (pendingScrollSync !== null) return;

  pendingScrollSync = requestAnimationFrame(() => {
    const header = calendarHeaderRef.value;
    const body = calendarContainerRef.value;

    if (header && body && lastScrollSource) {
      isProgrammaticScroll = true;

      if (lastScrollSource === 'header') {
        body.scrollLeft = header.scrollLeft;
      } else {
        header.scrollLeft = body.scrollLeft;
      }

      // Reset flag after a microtask to allow the scroll event to fire and be ignored
      queueMicrotask(() => {
        isProgrammaticScroll = false;
      });
    }

    pendingScrollSync = null;
    lastScrollSource = null;
  });
}

function onRemoteEventMouseEnter(event: MouseEvent, remoteEvent) {
  const popupEvent = {
    ...remoteEvent,
    time: {
      start: remoteEvent.start,
      end: remoteEvent.end,
    },
    customData: {
      calendar_title: remoteEvent.calendar_title,
      all_day: remoteEvent.all_day,
    },
  };

  popup.value = showEventPopup(event as any, popupEvent, 'right');
}

function onRemoteEventMouseLeave() {
  popup.value = { ...initialEventPopupData };
}

/**
 * Calculates grid positioning for calendar events
 * Returns position info including calculated height based on duration
 */
function calculateEventGridPosition(
  eventStart: Dayjs,
  eventEnd: Dayjs,
  slots: GridTimeSlot[],
  gap = 2,
  topOffset = 0
): GridElement {
  if (!slots.length) return null;

  // 1. Calculate grid column based on the event's position within the week
  // Get the weekdays array to determine the correct column position
  const { start } = props.activeDateRange;
  const startDate = dj(start);

  // Find the day of the week for this event (0-6, where 0 is the first day of the week)
  const eventDayOfWeek = eventStart.diff(startDate, 'day');

  // Safety check: ensure the event is within the displayed week range
  if (eventDayOfWeek < 0 || eventDayOfWeek > 6) {
    return null;
  }

  const gridColumn = eventDayOfWeek + 2; // +2 to account for the time column (column 1)

  // 2. Calculate the Start Row based on the hour
  const startHour = eventStart.hour();
  const gridRowStart = startHour + 1; // Row 1 is 00:00, Row 2 is 01:00, etc.

  // 3. Calculate vertical offset within the starting row (for events not starting on the hour)
  const startMinutes = eventStart.minute();
  const calculatedTopOffset = (startMinutes / 60) * ROW_HEIGHT_PX + topOffset;

  // 4. Calculate event duration and height
  // Handle events that cross midnight
  let durationMinutes = eventEnd.diff(eventStart, 'minute');
  if (durationMinutes <= 0) {
    // Event crosses midnight, calculate duration to end of day
    durationMinutes = 24 * 60 - (startHour * 60 + startMinutes);
  }

  // Height based on duration with minimum of 1.25rem (approximately 20px, 1rem for text and 0.25rem for padding)
  const calculatedHeight = (durationMinutes / 60) * ROW_HEIGHT_PX;
  const height = `max(1.25rem, ${calculatedHeight - gap}px)`;

  // Return the positioning data if the event falls within calendar hours
  if (gridRowStart >= 1 && gridRowStart <= 24) {
    return {
      gridColumn,
      gridRowStart,
      topOffset: `${calculatedTopOffset}px`, // Set design offset of 6px for each event
      height,
    } as GridElement;
  }

  return null;
}

/**
 * Weekdays is an array of date objects representing the current week
 */
const weekdays = computed(() => {
  const { start, end } = props.activeDateRange;

  // Access language setting to trigger recomputation when locale changes
  // The actual locale is set globally on dayjs, but we need this reactive dependency
  void userStore.data.settings.language;

  // Parse the start and end dates using dayjs's automatic parsing
  const startDate = dj(start);
  const endDate = dj(end);

  const days = [] as Dayjs[];
  let currentDate = startDate;

  // Loop from the start date until it's the same as the end date, inclusive
  while (!currentDate.isAfter(endDate, 'day')) {
    days.push(currentDate);

    // Move to the next day
    currentDate = currentDate.add(1, 'day');
  }

  return days;
});

/**
 * Generates an array of time slot objects for a full 24-hour day with grid positioning info
 */
const timeSlotsForGrid = computed(() => {
  const slots = [] as GridTimeSlot[];

  // Always show full 24-hour day with 60-minute intervals
  const startTime = dj('00:00', 'HH:mm');
  const endTime = dj('00:00', 'HH:mm').add(1, 'day');
  const slotDuration = 60;

  // Hour label format based on the browser's locale (e.g. "9 AM" or "09")
  const hourLabelFormat = hourFormat.value === 'HH:mm' ? 'HH:mm' : 'h A';

  let currentTime = startTime;

  // Start grid rows at 1
  let rowIndex = 1;

  while (currentTime.isBefore(endTime)) {
    // Create the slot for the current time
    slots.push({
      // The text to display (e.g., "9 AM"/"09" or empty for non-hour slots)
      text: currentTime.minute() === 0 ? currentTime.format(hourLabelFormat) : '',
      // The start time in a consistent format, useful for keys
      startTime: currentTime.format('HH:mm'),
      // Grid properties
      gridRowStart: rowIndex,
      gridRowEnd: rowIndex + 1,
    });

    currentTime = currentTime.add(slotDuration, 'minute');
    rowIndex++;
  }

  return slots;
});

/**
 * Generates an array of pending appointments that fall within the active week with grid positioning info
 */
const filteredPendingAppointmentsForGrid = computed(() => {
  const slots = timeSlotsForGrid.value;
  if (!slots.length) return [];

  const { start, end } = props.activeDateRange;
  const pendingAppointmentsWithinActiveWeek = props.pendingAppointments.filter((appointment) => {
    const appointmentStart = dj(appointment.slots[0].start);
    return appointmentStart.isBetween(start, end, 'day', '[]');
  });

  return pendingAppointmentsWithinActiveWeek
    .map((appointment) => {
      const appointmentStart = dj(appointment.slots[0].start);
      const appointmentEnd = appointmentStart.add(appointment.duration, 'minute');

      const gridPosition = calculateEventGridPosition(appointmentStart, appointmentEnd, slots, 2, 0);

      if (gridPosition) {
        return {
          ...appointment,
          ...gridPosition,

          // Add properties to match the remote event structure for consistent rendering
          title: appointment.title,
          start: appointment.slots[0].start,
          end: appointmentEnd.format(),
          calendar_title: appointment.calendar_title,
          calendar_color: appointment.calendar_color,
        };
      }
    })
    .filter(Boolean);
});

/**
 * Generates an array of remote events that fall within the active week with grid positioning info.
 * This intentionally excludes all-day events since those are not rendered inside the grid.
 * Note we are still fetching monthly events to reduce the roundtrips
 */
const filteredRemoteEventsForGrid = computed(() => {
  const slots = timeSlotsForGrid.value;
  if (!slots.length) return [];

  const { start, end } = props.activeDateRange;
  const remoteEventsWithinActiveWeek = props.events.filter(
    (remoteEvent) => dj(remoteEvent.start).isBetween(start, end, 'day', '[]') && !remoteEvent.all_day
  );

  return remoteEventsWithinActiveWeek
    .map((remoteEvent) => {
      const eventStart = dj(remoteEvent.start);
      const eventEnd = dj(remoteEvent.end);

      const gridPosition = calculateEventGridPosition(eventStart, eventEnd, slots, 2, 0);
      const hasPendingPlaceholder = filteredPendingAppointmentsForGrid.value.find(
        (a) => a.title === remoteEvent.title && eventStart.isSame(a.start)
      );

      // Only show remote event, if we have a valid grid position and if we don't already have calculated
      // a placeholder for a pending (HOLD) event
      if (gridPosition && !hasPendingPlaceholder) {
        return {
          ...remoteEvent,
          ...gridPosition,
        };
      }
    })
    .filter(Boolean);
});

/**
 * Generates an array of all-day remote events per weekday that fall within the active week.
 */
const filteredRemoteAllDayEvents = computed(() => {
  const slots = timeSlotsForGrid.value;
  if (!slots.length) return [];

  const { start, end } = props.activeDateRange;
  const remoteAllDayEventsWithinActiveWeek = props.events.filter(
    (remoteEvent) => dj(remoteEvent.start).isBetween(start, end, 'day', '[]') && remoteEvent.all_day
  );

  return weekdays.value.map((day) => {
    return remoteAllDayEventsWithinActiveWeek.filter((remoteEvent) => dj(remoteEvent.start).isSame(day, 'day'));
  });
});

/**
 * Generates an array of selectable time slots that fall within the active week with grid positioning info
 */
const filteredSelectableSlotsForGrid = computed(() => {
  const slots = timeSlotsForGrid.value;
  if (!slots.length) return [];

  const { start, end } = props.activeDateRange;
  const selectableSlotsWithinActiveWeek = props.selectableSlots
    .filter((slot) => {
      const slotStart = dj(slot.start);
      return slotStart.isBetween(start, end, 'day', '[]');
    })
    .filter((slot) => slot.booking_status !== BookingStatus.Booked);

  return selectableSlotsWithinActiveWeek
    .map((slot) => {
      const slotStart = dj(slot.start);
      const slotEnd = slotStart.add(slot.duration, 'minute');

      const gridPosition = calculateEventGridPosition(slotStart, slotEnd, slots, 0, 0);

      if (gridPosition) {
        return {
          ...slot,
          ...gridPosition,

          // Add properties to match the remote event structure for consistent rendering
          title: slotStart.format('LT'),
          start: slot.start,
          end: slotEnd.format(),
          calendar_title: '',
        };
      }
    })
    .filter(Boolean);
});

/**
 * Generates an array of blocked slots with grid positioning info
 */
const blockerPlaceholderForGrid = computed(() => {
  const slots = [] as GridElement[];

  // Go through the whole event grid. We currently fill the grid completely with blockers, to keep the time slots
  // consistent. We can however add conditions to skip blocked slots in the future if needed.
  for (let i = 1; i <= 24; i++) {
    for (let j = 2; j <= 8; j++) {
      const borderWidth = 1;

      slots.push({
        id: `${i}-${j}`,
        gridColumn: j,
        gridRowStart: i,
        topOffset: `${borderWidth}px`,
        height: `${ROW_HEIGHT_PX - borderWidth}px`,
      });
    }
  }

  return slots;
});

/**
 * Scrolls the calendar to the current time position
 */
function scrollToCurrentTime() {
  if (!calendarContainerRef.value) return;

  const now = dj();
  const currentHour = now.hour();

  // Find the slot that corresponds to the current hour
  const targetTime = `${String(currentHour).padStart(2, '0')}:00`;

  // Find the time slot element using its data-testid
  const targetElement = calendarContainerRef.value.querySelector(`[data-testid="time-${targetTime}"]`);

  if (targetElement) {
    // Get the element's offset position within the container
    const elementTop = (targetElement as HTMLElement).offsetTop;

    // Scroll to position the current time near the top (with some padding for the weekday header)
    calendarContainerRef.value.scrollTop = elementTop - 80;
  }
}

onMounted(() => {
  nextTick(() => {
    scrollToCurrentTime();
  });
});
</script>

<template>
  <div class="calendar-wrapper" :class="{ loading: isLoading, disabled: disabled }">
    <div class="calendar-loading" v-if="isLoading">
      <loading-spinner />
    </div>

    <!-- Header row (separate from scrollable grid) -->
    <div ref="calendarHeaderRef" class="calendar-header" @scroll="syncScroll('header')">
      <div class="corner-cell-block"></div>
      <div
        class="calendar-weekday-header"
        v-for="(weekday, index) in weekdays"
        :key="weekday.toISOString()"
        :style="{ gridColumn: index + 2 }"
      >
        <p>{{ weekday.format('ddd').toUpperCase() }}</p>
        <p>{{ weekday.date() }}</p>
        <div class="calendar-all-day-events">
          <div
            v-for="(allDayEvent, j) in filteredRemoteAllDayEvents[index]"
            :key="j"
            class="all-day-event-item"
            @mouseenter="(event) => onRemoteEventMouseEnter(event, allDayEvent)"
            @mouseleave="onRemoteEventMouseLeave"
          >
            {{ allDayEvent.title }}
          </div>
        </div>
      </div>
    </div>

    <!-- Scrollable grid body -->
    <div
      ref="calendarContainerRef"
      class="calendar-body"
      :style="{ 'grid-template-rows': `repeat(${timeSlotsForGrid.length}, minmax(60px, min-content))` }"
      @scroll="syncScroll('body')"
    >
      <!-- Left-side hourly blocks -->
      <div
        v-for="timeSlot in timeSlotsForGrid"
        class="time-slot-cell"
        :key="timeSlot.startTime"
        :style="{ gridRow: `${timeSlot.gridRowStart} / ${timeSlot.gridRowEnd}`, gridColumn: 1 }"
        :data-testid="`time-${timeSlot.startTime}`"
      >
        <!-- Omit 12 AM (00:00) label, show other hours at the top of the row -->
        <span v-if="timeSlot.text && timeSlot.startTime !== '00:00'" class="time-label">
          {{ timeSlot.text }}
        </span>
      </div>

      <!-- Blocked time slots -->
      <div
        v-for="blocker in blockerPlaceholderForGrid"
        :key="blocker?.id"
        class="event-item blocker-slot"
        :class="{ dark: isDarkMode }"
        :style="{
          gridColumn: blocker?.gridColumn,
          gridRowStart: blocker?.gridRowStart,
          marginTop: blocker?.topOffset,
          height: blocker?.height,
        }"
        :data-testid="`blocker-${dj(blocker.id)}`"
      ></div>

      <!-- Remote events -->
      <div
        v-for="remoteEvent in filteredRemoteEventsForGrid"
        :key="remoteEvent?.start"
        class="event-item remote-event"
        :class="{ dark: isDarkMode }"
        :style="{
          gridColumn: remoteEvent?.gridColumn,
          gridRowStart: remoteEvent?.gridRowStart,
          marginTop: remoteEvent?.topOffset,
          height: remoteEvent?.height,
        }"
        @mouseenter="(event) => onRemoteEventMouseEnter(event, remoteEvent)"
        @mouseleave="onRemoteEventMouseLeave"
        :data-testid="`remote-event-${dj(remoteEvent.start).format(DateFormatStrings.Qalendar)}`"
      >
        {{ remoteEvent?.title }}
      </div>

      <!-- Pending appointments -->
      <div
        v-for="pendingAppointment in filteredPendingAppointmentsForGrid"
        :key="pendingAppointment?.id"
        class="event-item pending-appointment"
        :class="{ dark: isDarkMode }"
        :style="{
          gridColumn: pendingAppointment?.gridColumn,
          gridRowStart: pendingAppointment?.gridRowStart,
          marginTop: pendingAppointment?.topOffset,
          height: pendingAppointment?.height,
        }"
        @mouseenter="(event) => onRemoteEventMouseEnter(event, pendingAppointment)"
        @mouseleave="onRemoteEventMouseLeave"
        :data-testid="`pending-appointment-${dj(pendingAppointment.start).format(DateFormatStrings.Qalendar)}`"
      >
        {{ pendingAppointment?.title }}
      </div>

      <!-- Selectable time slots -->
      <div
        v-for="slot in filteredSelectableSlotsForGrid"
        :key="slot?.id"
        class="event-item selectable-slot"
        :class="{ dark: isDarkMode, selected: (selectedEvent?.start as Dayjs)?.isSame(slot?.start) }"
        :style="{
          gridColumn: slot?.gridColumn,
          gridRowStart: slot?.gridRowStart,
          marginTop: slot?.topOffset,
          height: slot?.height,
        }"
        @click="!disabled ? emit('event-selected', slot.start) : null"
        :data-testid="`event-${dj(slot.start).format(DateFormatStrings.Qalendar)}`"
      >
        {{ slot?.title }}
      </div>

      <!-- Event popup (appears on remote event hover) -->
      <event-popup
        v-if="popup.event"
        :style="{
          display: popup.display,
          top: popup.top,
          left: popup.left,
          right: popup.right ?? 'initial',
          position: 'fixed',
          zIndex: 10,
        }"
        :event="popup.event"
        :position="popup.position || 'right'"
      />

      <!-- Inner grid vertical lines -->
      <div
        v-for="n in 8"
        :key="`line-${n}`"
        class="vertical-line"
        :style="{ gridColumn: n + 1, gridRow: '1 / -1' }"
      ></div>

      <!-- Inner grid horizontal lines (skip first since header has border-block-end) -->
      <div
        v-for="timeSlot in timeSlotsForGrid.slice(1)"
        :key="`h-line-${timeSlot.startTime}`"
        class="horizontal-line"
        :style="{ gridRow: timeSlot.gridRowStart, gridColumn: '2 / -1' }"
      ></div>
    </div>

    <div class="calendar-footer">
      <div class="corner-cell-block"></div>
      <hr class="calendar-container-bottom-border" />
    </div>
  </div>
</template>

<style scoped>
@import '@/assets/styles/custom-media.pcss';

.calendar-wrapper {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--colour-neutral-border);
  border-radius: 1.5rem;
  background-color: var(--colour-neutral-base);
  width: 100%;
  max-width: 100%;
  min-height: 505px;
  max-height: calc(100dvh - 400px);
  overflow: hidden;
  padding: 1rem;
  padding-block-start: 0;
  position: relative;

  &.loading {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .calendar-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
  }
}

.calendar-header {
  display: grid;
  grid-template-columns: 4rem repeat(7, 200px);
  justify-items: center;
  background-color: var(--colour-neutral-base);
  flex-shrink: 0;
  overflow-x: auto;

  /* Hide scrollbar but keep scrolling functional */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.calendar-weekday-header {
  padding-block-start: 2rem;
  text-align: center;
  font-weight: bold;
  width: 100%;
  background-color: var(--colour-neutral-base);
  color: var(--colour-ti-secondary);
  min-width: 200px;
  border-block-end: 1px solid var(--colour-neutral-border);

  /* Weekday */
  p:first-child {
    font-weight: normal;
    font-size: 0.68rem;
  }

  /* All day events */
  .calendar-all-day-events {
    padding-block: 0.5rem;
  }
}

.calendar-body {
  display: grid;
  grid-template-columns: 4rem repeat(7, 200px);
  justify-items: center;
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  position: relative;

  /* Hide scrollbar but keep scrolling functional */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.corner-cell-block {
  background-color: var(--colour-neutral-base);
  grid-column: 1;
  width: 100%;
  height: 100%;
  position: sticky;
  left: 0;
  z-index: 2;
}

.calendar-body .time-slot-cell {
  padding-inline: 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: sticky;
  left: 0;
  background-color: var(--colour-neutral-base);
  color: var(--colour-ti-secondary);
  font-size: 0.68rem;
  z-index: 9;

  .time-label {
    transform: translateY(-50%);
  }
}

.event-item {
  width: 95%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-block-start: 0.325rem;
  padding-inline-start: 0.5rem;
  padding-inline-end: 0.125rem;
  color: var(--colour-ti-secondary);
  font-size: 0.6875rem;
  border-left: solid 3px var(--colour-primary-default);
  border-radius: 0.1875rem; /* 3px */
  background-image: linear-gradient(
    159deg,
    color-mix(in srgb, var(--colour-primary-default) 20%, transparent) 13%,
    color-mix(in srgb, var(--colour-accent-blue) 20%, transparent) 99%
  );
  cursor: pointer;
  z-index: 1;
  min-width: 200px;
  align-self: start;
  justify-self: start;
  box-sizing: border-box;

  &.dark {
    background-color: var(--colour-neutral-raised);
  }
}

.all-day-event-item {
  width: 95%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: var(--colour-primary-default);
  color: var(--colour-ti-base-dark);
  border-radius: 0.1875rem; /* 3px */
  box-sizing: border-box;
  padding-inline-start: 0.25rem;
  font-weight: normal;
  font-size: 0.68rem;
  margin-block-end: 0.125rem;
}

.pending-appointment {
  border: 1px dashed color-mix(in srgb, var(--colour-primary-default) 66%, transparent);
  background-color: color-mix(in srgb, var(--colour-primary-default) 10%, transparent);
  background-image: none;

  z-index: 4;

  &.dark {
    border-color: var(--colour-primary-default);
  }
}

.remote-event {
  z-index: 4;
}

.selectable-slot {
  margin: 0;
  width: 100%;

  background-color: var(--colour-neutral-base);
  background-image: none;
  border: none;
  border-radius: 0;
  color: transparent;

  &.selected {
    background-color: var(--colour-accent-blue);
    border: 1px dashed color-mix(in srgb, var(--colour-primary-default) 80%, transparent);
    border-radius: 0.1875rem; /* 3px */
    color: var(--colour-neutral-base);

    z-index: 4;
  }
}
.calendar-wrapper.disabled .selectable-slot {
  cursor: default;
}
.calendar-wrapper:not(.disabled) .selectable-slot:not(.selected):hover {
  background-color: color-mix(in srgb, var(--colour-ti-highlight) 10%, var(--colour-neutral-base));
  border: 1px dashed color-mix(in srgb, var(--colour-primary-default) 66%, transparent);
  border-radius: 0.1875rem; /* 3px */

  z-index: 4;
}

.blocker-slot {
  --stripe: color-mix(in srgb, var(--colour-neutral-border) 50%, transparent);

  border: none;
  margin: 0;
  width: calc(100% - 1px);
  margin-inline-start: 1px;
  border-radius: 0;
  background-color: var(--colour-neutral-lower);
  background-image: repeating-linear-gradient(
    135deg,
    transparent 0px,
    transparent 3px,
    var(--stripe) 3px,
    var(--stripe) 4px,
    transparent 4px,
    transparent 8px
  );
  cursor: default;

  &.dark {
    --stripe: var(--colour-neutral-base);
    background-color: color-mix(in srgb, var(--colour-neutral-lower) 66%, transparent);
  }
}

.vertical-line {
  justify-self: flex-start;
  border-left: 1px solid var(--colour-neutral-border);
  position: relative;
  z-index: 2;
}

.horizontal-line {
  height: 1px;
  background-color: var(--colour-neutral-border);
  width: 100%;
  position: relative;
  z-index: 2;
}

.calendar-footer {
  display: grid;
  grid-template-columns: 4rem repeat(7, minmax(0, 1fr));

  .calendar-container-bottom-border {
    border: none;
    border-block-end: 1px solid var(--colour-neutral-border);
    grid-column: 2 / -1;
  }
}

@media (--md) {
  .calendar-header {
    grid-template-columns: 4rem repeat(7, minmax(0, 1fr));
    overflow-x: visible;
  }

  .calendar-body {
    grid-template-columns: 4rem repeat(7, minmax(0, 1fr));
    overflow-x: visible;
  }

  .calendar-weekday-header {
    min-width: auto;
  }

  .time-slot-cell {
    position: static;
    left: auto;
    background-color: transparent;
    z-index: auto;

    .time-label {
      transform: translateY(-50%);
    }
  }

  .event-item {
    min-width: auto;
  }

  .vertical-line {
    z-index: 3;
  }
}
</style>
